import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signOut as fbSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { firebaseAuth } from './firebase';
import { http } from '@/core/http/client';

export type Role = 'empresa' | 'super_admin' | 'admin' | 'operator';

/**
 * El rol, en la lengua del producto.
 *
 * Vive junto al tipo y no en `admin/users` porque lo lee también la cabecera de
 * la app de campo, que no debe importar de un módulo de administración.
 */
export const ROLE_LABELS: Record<Role, string> = {
  empresa: 'Empresa (Zerep)',
  super_admin: 'Super admin',
  admin: 'Admin',
  operator: 'Operador',
};

export interface AppUser {
  id: string;
  firebase_uid: string;
  company_id: string;
  email: string;
  name: string;
  phone: string | null;
  role: Role;
  active: boolean;
  /** El avatar elegido, o `null` mientras se quede con sus iniciales. */
  avatar: string | null;
}

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  user: AppUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /**
   * Adopta la versión recién guardada de la propia cuenta.
   *
   * La respuesta de `PATCH /users/me` es la misma forma que `/users/me`, así
   * que sirve de fuente sin una segunda vuelta al servidor. Y hace falta: sin
   * esto, cambiar el nombre en el perfil dejaba el de antes en la barra lateral
   * hasta recargar la página.
   *
   * No acepta `null` a propósito: salir de la sesión es cosa de Firebase, y un
   * setter que admitiera vaciar al usuario dejaría a media app pensando que
   * nadie ha entrado.
   */
  applyUser: (user: AppUser) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      // We have a Firebase identity — try to resolve the backend User.
      setLoading(true);
      try {
        const { data } = await http.get<AppUser>('/users/me');
        setUser(data);
      } catch (err) {
        // 403 here usually means the user isn't provisioned in Mongo yet.
        // Surface to the console so it's discoverable without devtools magic.
        // eslint-disable-next-line no-console
        console.error('[auth] /users/me failed:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      user,
      loading,
      signOut: async () => {
        await fbSignOut(firebaseAuth);
      },
      applyUser: setUser,
    }),
    [firebaseUser, user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within <AuthProvider>');
  return ctx;
}
